import classNames from "classnames"
import Image from 'next/image'
import Link from "next/link"
import { useDispatch } from "react-redux"
import { Card, CardBody, CardFooter, CardHeader } from "reactstrap"

import { IProject, IProjectReport, ProjectProgress } from "@api/schema"
import Icon from "@components/common/Icon"
import LinkLookalike from "@components/common/LinkLookalike"
import { withDynamicNamespaces } from "@components/hoc/withDynamicNamespaces"
import ProjectStatusIndicator from "@components/marketplace/projects/ProjectStatusIndicator"
import ProjectCreatorText from "@components/project/ProjectCreatorText"
import ProjectNameText from "@components/project/ProjectNameText"
import { addNotificationAction } from "@redux/actions/notifications"
import { IFormikActions } from "@redux/helper/actions"
import { reportProjectAction } from "@redux/usecases/marketplace"
import { useToggleState } from "@services/hooks/useToggleState"
import { NamespaceShortcut, useDynamicTranslation } from "@services/i18n"
import { deletedProjectShortDescription, projectIllustrationPlaceholder } from "@services/projectHelper"
import { Routes, routeWithParams } from "@services/routes"
import { useTooltip } from "@services/tooltipHook"
import { iriAsHtmlId } from "@services/util"
import { prefixApiUrl } from "@services/util"
import { BASE_URL, MODULE_PICTURE_IN_PROJECT_CARD } from "config"

import ReportProjectModal from "./ReportProjectModal"

const usedNamespaces: NamespaceShortcut[] = ["common", "follow-project"]

interface IProps {
  project: IProject
  noLinkButMisuseReport?: boolean // should the card be a link? -> not for ideas when already chosen to create a project from them; instead: show misuseReport-Button
}

/**
 * this components provide a card for a project for the marketplace
 *
 * states to be handled:
 * project.implementationstate
 * project.progress
 * project.state
 * project.locked
 *
 * The card works as a link TO "create project from idea" when used in marketplace
 * but should not be a link when used ON the "create project from idea" page, instead: show misuseReport-Button.
 * Therefor the notLinkButMisuseReport param should be used.
 */
const ProjectCard: React.FC<IProps> = (props: IProps) => {
  const { project, noLinkButMisuseReport = false } = props
  const t = useDynamicTranslation()

  const dispatch = useDispatch()

  const { tooltipTargetRef: ideaTooltipRef, ToolTip: IdeaTooltip } = useTooltip(t("common", "goto.takeIdea"))
  const { tooltipTargetRef: misuseTooltipRef, ToolTip: MisuseTooltip } = useTooltip(t("common", "page.projects.index.reportProjectHeading"))

  const { isTrue: reportModalOpen, toggle: toggleReportModal } = useToggleState(false)

  const onReportSubmit = (report: IProjectReport, actions: IFormikActions): void => {
    actions.onSuccess = () => {
      dispatch(addNotificationAction("message.project.reported", "success"))
      toggleReportModal()
    }
    dispatch(reportProjectAction(project, report, actions))
  }


  // this card has different Routes if it is an Idea or an Project
  // or if it is an unsaved Idea while in creating process
  const cardRoute = project.id ?
    project.progress !== ProjectProgress.Idea
      ? routeWithParams(Routes.ProjectPage, { slug: project.slug || project.id })
      : routeWithParams(Routes.CreateProject, { id: project.id })
    : null

  const classname = classNames("project-card",
    { "dancing clickable": !noLinkButMisuseReport }
  )
  const componentId = iriAsHtmlId(project)

  // the card, to be wrapped into a link or not
  const card = <Card
    className={classname}
    id={componentId}
    innerRef={ideaTooltipRef}
  >
    {// cards for projects get a header with title, cards for ideas do not
      project.progress !== ProjectProgress.Idea &&
      <CardHeader>
        {MODULE_PICTURE_IN_PROJECT_CARD && <div className="project-card__thumbnail">
          <Image
            // uses project picture if given or placeholder
            src={prefixApiUrl(project.picture?.contentUrl || (BASE_URL + projectIllustrationPlaceholder.path))}
            alt={t("common", "project.picture.label")}
            width={project.picture?.dimensions[0] || projectIllustrationPlaceholder.width}
            height={project.picture?.dimensions[1] || projectIllustrationPlaceholder.height}
            priority
            layout="fill"
          />
        </div>}
        <div className="title-section">
          <h3>
            <LinkLookalike>
              <ProjectNameText project={project} />
            </LinkLookalike>
          </h3>
          <span className="project-information"><ProjectCreatorText project={project} /></span>
        </div>
      </CardHeader>
    }
    <CardBody>
      <p>{project.shortDescription}</p>
    </CardBody>

    <CardFooter>
      <div className="d-flex flex-column flex-md-row justify-content-between">
        <ProjectStatusIndicator project={project} simple />
        <div className="d-flex align-items-center">
          {project.progress !== ProjectProgress.Idea
            ? <>
              <Icon
                name="thumbs-up" size={18}
                title={t("follow-project", "followProject")}
              />
              <span className="font-weight-bold ml-2">
                {project.followerCount || 0}
              </span>
              <Icon
                name="user-multiple" size={18}
                title={t("common", "project.memberships.count")}
                className="ml-2"
              />
              <span className="font-weight-bold ml-2">
                {project.memberCount || 0}
              </span>
            </>
            :  // show "resulting projects" or "take idea" icon only, if the idea is not in creation process but saved (means: has gotten an id)
            project.id && <>
              {/*
                  // @todo: unused (19.01.2023): unklarer Mehrwert
                  <Icon
                    name="clock" size={18}
                    title={t("project.daysOnline.count")}
                  />
                  <span className="font-weight-bold ml-2">
                    {Math.ceil((new Date().getTime() - new Date(project.createdAt).getTime()) / (1000 * 24 * 60 * 60))}
                  </span>
              */}
              <span className="font-weight-bold ml-2">
                {project.resultingProjects?.filter(p => p.shortDescription !== deletedProjectShortDescription).length > 0 && <>
                  <Icon
                    name="take-idea" size={18}
                  />
                  {project.resultingProjects?.filter(p => p.shortDescription !== deletedProjectShortDescription).length}
                </>}
              </span>
            </>
          }
        </div>
      </div>
    </CardFooter>
  </Card>

  return <div>
    {// only show link, if there is a route (may not happen, if the idea does not have an id yet) or if there should be a misuse button instead of a link
      noLinkButMisuseReport || !cardRoute
        ? <>{card}
          {noLinkButMisuseReport && <>
            <div
              className="idea-report"
              ref={misuseTooltipRef}
            >
              <button
                className="actionicon"
                title={t("common", "page.projects.index.reportProjectHeading")}
                onClick={toggleReportModal}
              >
                <Icon name="flag" size={20} />
              </button>
            </div>
            <ReportProjectModal
              modalOpen={reportModalOpen}
              isIdea={true}
              onSubmit={onReportSubmit}
              toggle={toggleReportModal}
            />
            {MisuseTooltip}
          </>}
        </>
        : <>
          <Link href={cardRoute}><a>
            {card}
          </a></Link>
          {project.progress === ProjectProgress.Idea && IdeaTooltip}
        </>
    }
  </div>
}

export default withDynamicNamespaces<IProps>(ProjectCard, usedNamespaces)
